import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getStatusMetadata } from '../../vacations.definitions';
import { isPendingStatus } from '../../utils/vacationClientPeriods';
import type { VacationRequestStatus } from '../../models/vacation.types';
import type { TeamVacationOverlap } from '../../models/vacationClientView.types';

export const PANEL_STATUS_LABELS: Partial<Record<VacationRequestStatus, string>> = {
    Scheduled: 'Agendada',
    OnVacation: 'Em férias',
    Completed: 'Concluída',
    PendingManager: 'Com gestor',
    PendingClient: 'Com cliente',
    PendingDP: 'Aguardando DP',
};

export const PENDING_SECTION_TITLE = 'Aguardando aprovação';
export const APPROVED_SECTION_TITLE = 'Agendadas e em férias';

export function getStatusBadgeClass(status: VacationRequestStatus): string {
    const metadata = getStatusMetadata(status);
    return metadata.className;
}

export function getPanelStatusLabel(status: VacationRequestStatus): string {
    return PANEL_STATUS_LABELS[status] ?? status;
}

export function formatPeriodRange(start: Date, end: Date): string {
    return `${format(start, 'dd/MMM', { locale: ptBR })} a ${format(end, 'dd/MMM', { locale: ptBR })}`;
}

export const OVERLAP_BADGE_PREFIX = 'Sobrepõe com';
export const NEARBY_BADGE_PREFIX = 'Próximo de';
export const SCHEDULED_BADGE_PREFIX = 'Já agendado no período:';
export const NO_CONFLICTS_LABEL = 'Sem conflitos no período';
export const SHOW_DETAILS_LABEL = 'Ver detalhes';
export const HIDE_DETAILS_LABEL = 'Ocultar detalhes';
export const VIEW_REQUEST_LABEL = 'Ver solicitação →';
export const RESPONSIBLE_LABEL = 'Responsável:';
export const RESPONSIBLE_SECTION_LABEL = 'Responsável:';

export const HEADER_PILL_LABELS = {
    people: (count: number) => `${count} pessoa${count !== 1 ? 's' : ''}`,
    pending: (count: number) => `${count} aguardando aprovação`,
    overlap: (count: number) => `${count} com conflito`,
};

export const HEADER_PILL_HINTS = {
    people: 'Pessoas deste cliente com férias agendadas, em férias ou aguardando aprovação no período filtrado.',
    pending: 'Solicitações ainda paradas com gestor, cliente ou DP no período filtrado.',
    overlap: 'Solicitações que se sobrepõem ou ficam a até 7 dias das férias de outra pessoa do mesmo cliente.',
};

export const RESPONSIBLE_PILL_LABELS = {
    people: (count: number) => `${count} pessoa${count !== 1 ? 's' : ''}`,
    pending: (count: number) => `${count} aguardando aprovação`,
    overlap: (count: number) => `${count} com conflito`,
};

export const RESPONSIBLE_PILL_HINTS = {
    people: 'Pessoas deste responsável com férias agendadas, em férias ou aguardando aprovação no período filtrado.',
    pending: 'Solicitações ainda paradas com gestor, cliente ou DP no período filtrado.',
    overlap: 'Solicitações que se sobrepõem ou ficam a até 7 dias das férias de outra pessoa do mesmo cliente.',
};

export const CALENDAR_BAR_CLASSES = {
    self: 'bg-primary-500',
    overlap: 'bg-amber-400',
    nearby: 'bg-sky-400',
    scheduled: 'ring-1 ring-inset ring-blue-400',
    onVacation: 'ring-1 ring-inset ring-emerald-500',
};

export const SCHEDULED_BAR_STRIPE_STYLE = {
    backgroundImage:
        'repeating-linear-gradient(135deg, rgba(96,165,250,0.9) 0px, rgba(96,165,250,0.9) 4px, rgba(191,219,254,0.9) 4px, rgba(191,219,254,0.9) 8px)',
};

export const ON_VACATION_BAR_STRIPE_STYLE = {
    backgroundImage:
        'repeating-linear-gradient(135deg, rgba(34,197,94,0.9) 0px, rgba(34,197,94,0.9) 4px, rgba(187,247,208,0.9) 4px, rgba(187,247,208,0.9) 8px)',
};


export function isScheduledColleague(status: VacationRequestStatus): boolean {
    return !isPendingStatus(status);
}

export function barToneStyle(tone: string): Record<string, string> {
    if (tone === 'scheduled') return SCHEDULED_BAR_STRIPE_STYLE;
    if (tone === 'onVacation') return ON_VACATION_BAR_STRIPE_STYLE;
    return {};
}

export { getSegmentTitle } from '../../utils/vacationSegments';

export const HEADER_PEOPLE_PILL_CLASS = 'bg-raro-gray-100 text-raro-gray-600';
export const HEADER_PENDING_PILL_CLASS = 'bg-primary-100 text-primary-700';
export const HEADER_OVERLAP_PILL_CLASS = 'bg-amber-100 text-amber-700';

export const RESPONSIBLE_PEOPLE_PILL_CLASS = 'bg-white text-raro-gray-700';
export const RESPONSIBLE_PENDING_PILL_CLASS = 'bg-white text-primary-700';
export const RESPONSIBLE_OVERLAP_PILL_CLASS = 'bg-white text-amber-700';

export const OVERLAP_BADGE_CLASS = 'bg-amber-100 text-amber-800';
export const NEARBY_BADGE_CLASS = 'bg-sky-100 text-sky-700';
export const SCHEDULED_BADGE_CLASS = 'bg-blue-100 text-blue-800';

export const PERIOD_CARD_CONFLICT_BORDER_CLASS = 'border-l-amber-400';
export const PERIOD_CARD_SCHEDULED_BORDER_CLASS = 'border-l-blue-300';
export const PERIOD_CARD_DEFAULT_BORDER_CLASS = 'border-l-raro-gray-200';

export interface GroupedConflictNames {
    overlapping: string[];
    nearby: string[];
     scheduled: string[];
}

export function groupConflictNames(overlaps: TeamVacationOverlap[]): GroupedConflictNames {
    const overlapping = new Map<string, string>();
    const nearby = new Map<string, string>();
    const scheduled = new Map<string, string>();

    overlaps.forEach((overlap) => {
        if (isScheduledColleague(overlap.colleagueStatus)) {
            scheduled.set(overlap.collaboratorId, overlap.collaboratorName);
            return;
        }
        if (overlap.proximity === 'overlap') {
            overlapping.set(overlap.collaboratorId, overlap.collaboratorName);
        } else if (overlap.proximity === 'nearby' && !overlapping.has(overlap.collaboratorId)) {
            nearby.set(overlap.collaboratorId, overlap.collaboratorName);
        }
    });

    overlapping.forEach((_, collaboratorId) => nearby.delete(collaboratorId));

    return {
        overlapping: Array.from(overlapping.values()),
        nearby: Array.from(nearby.values()),
        scheduled: Array.from(scheduled.values()),
    };
}

const nameListFormatter = new Intl.ListFormat('pt-BR', { style: 'long', type: 'conjunction' });

export function formatNameList(names: string[]): string {
    return nameListFormatter.format(names);
}

export const BADGE_MAX_NAMES = 3;

export function formatBadgeNames(names: string[], max: number = BADGE_MAX_NAMES): string {
    if (names.length <= max) return formatNameList(names);
    const rest = names.length - max;
    return `${names.slice(0, max).join(', ')} e mais ${rest} pessoa${rest !== 1 ? 's' : ''}`;
}
